import React, { useEffect, useRef } from "react";
import {
  Switch,
  Route,
  Redirect,
  useRouteMatch,
  useLocation,
  useHistory,
} from "react-router-dom";
import { PERFORMANCE_NAVIGATION_TABS } from "../../../utils/Constants";
import ConversionComponent from "./ConversionComponent";
import RevenueComponent from "./RevenueComponent";
import ProductComponent from "./ProductComponent";
import TrafficComponent from "./TrafficComponent";
import AnalysisComponent from "./AnalysisComponent";
import PrimaryToolbarSequence from "components/PrimaryToolbarSequence";
import PerformanceNavigationTabs from "components/PerformanceNavigationTabs";
import {
  selectCallerAccountId,
  selectIsKPIExecutiveSummaryFeatureEnabled,
} from "store/app-config/AppConfigSelectors";
import { useDispatch, useSelector } from "react-redux";
import { selectUserLocale } from "store/user/UserSelectors";
import {
  setPerformanceReportDateRange,
  fetchKPIAnalysis,
  loadKPIStoreList,
} from "store/performance-report/PerformanceReportActions";
import {
  selectPerformanceReportStoreId,
  selectIsReportAPIAvailable,
  selectKPIStoreListLoaded,
} from "store/performance-report/PerformanceReportSelectors";
import { loadKpiConfig } from "store/kpi-config/KpiConfigActions";
import {
  selectKpiConfigIsLoaded,
  selectKpiConfigIsLoading,
  selectKpiCategories,
} from "store/kpi-config/KpiConfigSelectors";
import { getCurrentDate } from "utils/PerformanceReportUtils";
import { useHasEntitlement } from "hooks/useEntitlement";
import { selectStoresLoadedState } from "store/store-list/StoreListSelectors";
import { LoadStoreList } from "store/store-list/StoreListActions";

const PerformanceContainer: React.FC = () => {
  const dispatch = useDispatch();
  const { url } = useRouteMatch();
  const location = useLocation();
  const history = useHistory();
  const selectedAccount = useSelector(selectCallerAccountId);
  const isStoresLoaded = useSelector(selectStoresLoadedState);
  const isKPIStoreListLoaded = useSelector(selectKPIStoreListLoaded);
  const isKPIExecutiveSummaryFeatureEnabled = useSelector(
    selectIsKPIExecutiveSummaryFeatureEnabled,
  );
  const performanceStoreId = useSelector(selectPerformanceReportStoreId);
  const isReportAPIAvailable = useSelector(selectIsReportAPIAvailable);
  const hasAiEntitlement = useHasEntitlement("ai");
  const hasCatalogEntitlement = useHasEntitlement("catalog");
  const isKpiConfigLoaded = useSelector(selectKpiConfigIsLoaded);
  const isKpiConfigLoading = useSelector(selectKpiConfigIsLoading);
  const userLocale = useSelector(selectUserLocale);
  const kpiCategories = useSelector(selectKpiCategories);
  const lastRedirectedStoreId = useRef<string | null>(null);

  const defaultTab =
    isKpiConfigLoaded && kpiCategories.length > 0
      ? kpiCategories[0].id
      : PERFORMANCE_NAVIGATION_TABS.TRAFFIC;

  // Check if we're on the Executive Summary tab
  const isSummaryTab = location.pathname.includes(
    PERFORMANCE_NAVIGATION_TABS.EXECUTIVE_SUMMARY,
  );

  useEffect(() => {
    dispatch(setPerformanceReportDateRange({ dateRange: "last_7_days" }));
    if (hasCatalogEntitlement) {
      if (!isStoresLoaded) {
        dispatch(LoadStoreList());
      }
    } else if (!isKPIStoreListLoaded) {
      dispatch(loadKPIStoreList());
    }
  }, [dispatch, isKPIStoreListLoaded, hasCatalogEntitlement, isStoresLoaded]);

  useEffect(() => {
    if (performanceStoreId && !isKpiConfigLoaded && !isKpiConfigLoading) {
      dispatch(
        loadKpiConfig({
          storeId: performanceStoreId,
          language: userLocale || "en-US",
        }),
      );
    }
  }, [
    dispatch,
    performanceStoreId,
    isKpiConfigLoaded,
    isKpiConfigLoading,
    userLocale,
  ]);

  useEffect(() => {
    if (
      isKpiConfigLoaded &&
      kpiCategories.length > 0 &&
      performanceStoreId &&
      lastRedirectedStoreId.current !== performanceStoreId
    ) {
      const firstCategory = kpiCategories[0].id;
      const currentPath = location.pathname;
      const isOnAnalysisTab = currentPath.includes(
        PERFORMANCE_NAVIGATION_TABS.EXECUTIVE_SUMMARY,
      );

      if (!currentPath.includes(`/${firstCategory}`) && !isOnAnalysisTab) {
        lastRedirectedStoreId.current = performanceStoreId;
        history.replace({
          pathname: `${url}/${firstCategory}`,
          search: location.search,
        });
      } else {
        lastRedirectedStoreId.current = performanceStoreId;
      }
    }
  }, [
    isKpiConfigLoaded,
    kpiCategories,
    performanceStoreId,
    url,
    history,
    location,
  ]);

  useEffect(() => {
    if (
      isKPIExecutiveSummaryFeatureEnabled &&
      performanceStoreId &&
      isReportAPIAvailable
    ) {
      const analysisDateRange = getCurrentDate();
      dispatch(
        fetchKPIAnalysis({
          storeId: performanceStoreId,
          date: analysisDateRange,
        }),
      );
    }
  }, [
    dispatch,
    isKPIExecutiveSummaryFeatureEnabled,
    performanceStoreId,
    isReportAPIAvailable,
  ]);

  return (
    <>
      {selectedAccount !== "" && (
        <PrimaryToolbarSequence
          navigationTabs={() => <PerformanceNavigationTabs />}
          showTopNavigation={true}
          disableDateRangeSelector={isSummaryTab}
        />
      )}
      <Switch>
        <Route path={`${url}/${PERFORMANCE_NAVIGATION_TABS.EXECUTIVE_SUMMARY}`}>
          {isKPIExecutiveSummaryFeatureEnabled && hasAiEntitlement ? (
            <AnalysisComponent />
          ) : (
            <Redirect
              to={{
                pathname: `${url}/${defaultTab}`,
                search: location.search,
              }}
            />
          )}
        </Route>
        <Route path={`${url}/${PERFORMANCE_NAVIGATION_TABS.TRAFFIC}`}>
          <TrafficComponent />
        </Route>
        <Route path={`${url}/${PERFORMANCE_NAVIGATION_TABS.PRODUCT}`}>
          <ProductComponent />
        </Route>
        <Route path={`${url}/${PERFORMANCE_NAVIGATION_TABS.CONVERSION}`}>
          <ConversionComponent />
        </Route>
        <Route path={`${url}/${PERFORMANCE_NAVIGATION_TABS.REVENUE}`}>
          <RevenueComponent />
        </Route>
        <Redirect
          to={{
            pathname: `${url}/${defaultTab}`,
            search: location.search,
          }}
        />
      </Switch>
    </>
  );
};

export default PerformanceContainer;
